import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { Stage, Layer, Image as KonvaImage, Rect, Text, Transformer } from 'react-konva';
import useImage from 'use-image';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Save, Plus, Trash2, MousePointer2, ArrowLeft, Minus, Maximize } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const SUPER_ADMIN_ID = "27ad7808-0154-43cb-b39e-9b425c41d68d";

interface MenuUpload {
  id: string;
  file_url: string;
  file_name: string;
  branch_id: string;
  visual_mapping: any;
}

interface MenuItem {
  id: string;
  name: string;
  base_price: number;
}

interface Overlay {
  id: string;
  item_id: string;
  type: 'price' | 'name' | 'description';
  x: number;
  y: number;
  width: number;
  height: number;
  fontSize: number;
  fontFamily: string;
  color: string;
  fontWeight: string;
  textAlign: 'left' | 'center' | 'right';
}

const URLImage = ({ src, x, y, width, height }: any) => {
  const [image] = useImage(src, 'anonymous'); // Use anonymous crossOrigin for signed URLs
  return <KonvaImage image={image} x={x} y={y} width={width} height={height} />;
};

const OverlayRect = ({ shapeProps, isSelected, onSelect, onChange }: any) => {
  const shapeRef = useRef<any>();
  const trRef = useRef<any>();

  useEffect(() => {
    if (isSelected) {
      trRef.current.nodes([shapeRef.current]);
      trRef.current.getLayer().batchDraw();
    }
  }, [isSelected]);

  return (
    <React.Fragment>
      <Rect
        onClick={onSelect}
        onTap={onSelect}
        ref={shapeRef}
        {...shapeProps}
        draggable
        fill="rgba(0, 150, 255, 0.3)"
        stroke={isSelected ? "#0096FF" : "transparent"}
        strokeWidth={2}
        onDragEnd={(e) => {
          onChange({
            ...shapeProps,
            x: e.target.x(),
            y: e.target.y(),
          });
        }}
        onTransformEnd={(e) => {
          const node = shapeRef.current;
          const scaleX = node.scaleX();
          const scaleY = node.scaleY();

          node.scaleX(1);
          node.scaleY(1);
          onChange({
            ...shapeProps,
            x: node.x(),
            y: node.y(),
            width: Math.max(5, node.width() * scaleX),
            height: Math.max(5, node.height() * scaleY),
          });
        }}
      />
      {isSelected && (
        <Transformer
          ref={trRef}
          boundBoxFunc={(oldBox, newBox) => {
            if (newBox.width < 5 || newBox.height < 5) {
              return oldBox;
            }
            return newBox;
          }}
        />
      )}
    </React.Fragment>
  );
};

export default function VisualMapper() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [uploads, setUploads] = useState<MenuUpload[]>([]);
  const [selectedUploadId, setSelectedUploadId] = useState<string | null>(null);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [overlays, setOverlays] = useState<Overlay[]>([]);
  const [selectedOverlayId, setSelectedOverlayId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [branches, setBranches] = useState<{id: string, name: string}[]>([]);
  const [selectedBranchId, setSelectedBranchId] = useState<string | null>(null);
  
  // Canvas state
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [scale, setScale] = useState(1);
  const stageRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const fitToScreen = () => {
    const container = containerRef.current;
    if (container && imageSize.width > 0 && imageSize.height > 0) {
      const padding = 64; // p-8 * 2
      const availableWidth = container.clientWidth - padding;
      const availableHeight = container.clientHeight - padding;
      
      const scaleX = availableWidth / imageSize.width;
      const scaleY = availableHeight / imageSize.height;
      
      const newScale = Math.min(scaleX, scaleY, 0.9); // 0.9 to leave some margin
      setScale(newScale);
    }
  };

  useEffect(() => {
    if (imageSize.width > 0 && imageSize.height > 0) {
      fitToScreen();
    }
  }, [imageSize]);

  useEffect(() => {
    if (user && user.id === SUPER_ADMIN_ID) {
      fetchBranches();
    }
  }, [user]);

  useEffect(() => {
    if (selectedBranchId) {
      fetchUploads(selectedBranchId);
      fetchMenuItems(selectedBranchId);
    } else {
      setUploads([]);
      setMenuItems([]);
    }
  }, [selectedBranchId]);

  const [imageUrl, setImageUrl] = useState<string | null>(null);

  useEffect(() => {
    if (selectedUploadId) {
      const upload = uploads.find(u => u.id === selectedUploadId);
      if (upload) {
        // Check if file is PDF
        const isPdf = upload.file_name.toLowerCase().endsWith('.pdf') || 
                      upload.file_url.toLowerCase().includes('.pdf');
        
        if (isPdf) {
          toast({ 
            title: 'PDF Not Supported', 
            description: 'The Visual Mapper currently only supports image files (JPG, PNG). Please convert your PDF to an image to map it.', 
            variant: 'destructive' 
          });
          setImageUrl(null);
          return;
        }

        // Load existing mapping if any
        if (upload.visual_mapping && upload.visual_mapping.overlays) {
          setOverlays(upload.visual_mapping.overlays);
        } else {
          setOverlays([]);
        }
        
        // Load image using signed URL to bypass access restrictions
        const loadImage = async () => {
          try {
            console.log('Fetching image from:', upload.file_url);
            
            // Extract path from URL
            const getPathFromUrl = (urlStr: string) => {
              try {
                const url = new URL(urlStr);
                // Expected format: .../storage/v1/object/public/menu-uploads/path/to/file
                const parts = url.pathname.split('/menu-uploads/');
                if (parts.length > 1) {
                  return decodeURIComponent(parts[1]);
                }
                return null;
              } catch {
                return null;
              }
            };

            const storagePath = getPathFromUrl(upload.file_url);
            
            if (!storagePath) {
              throw new Error('Could not extract storage path from URL');
            }
            
            console.log('Storage path:', storagePath);
            
            // First check if file exists by trying to get its metadata
            const { data: listData, error: listError } = await supabase.storage
              .from('menu-uploads')
              .list(storagePath.split('/')[0], {
                search: storagePath.split('/')[1]
              });
            
            if (listError || !listData || listData.length === 0) {
              console.error('File does not exist in storage:', storagePath);
              toast({ 
                title: 'File Not Found', 
                description: 'This menu image no longer exists in storage. It may have been deleted.', 
                variant: 'destructive' 
              });
              return;
            }
            
            // Create a signed URL (valid for 1 hour) - this bypasses public access restrictions
            const { data: signedUrlData, error: signedUrlError } = await supabase.storage
              .from('menu-uploads')
              .createSignedUrl(storagePath, 3600); // 1 hour expiry
              
            if (signedUrlError || !signedUrlData?.signedUrl) {
              console.error('Failed to create signed URL:', signedUrlError);
              toast({ 
                title: 'Storage Error', 
                description: 'Could not access this file. It may have been deleted from storage.', 
                variant: 'destructive' 
              });
              return;
            }
            
            console.log('Got signed URL, loading image...');
            
            // Load the image using the signed URL
            const img = new window.Image();
            img.crossOrigin = 'anonymous';
            
            img.onload = () => {
              console.log('Image loaded successfully:', img.naturalWidth, 'x', img.naturalHeight);
              setImageUrl(signedUrlData.signedUrl);
              setImageSize({ width: img.naturalWidth, height: img.naturalHeight });
            };
            
            img.onerror = (e) => {
              console.error('Image failed to load:', e);
              toast({ title: 'Error', description: 'Failed to load menu image', variant: 'destructive' });
            };
            
            img.src = signedUrlData.signedUrl;
            
          } catch (error) {
            console.error('Error loading image:', error);
            toast({ title: 'Error', description: 'Failed to load menu image. File may not exist.', variant: 'destructive' });
          }
        };
        
        loadImage();
      }
    }
    
    // No cleanup needed for signed URLs (they're not blob URLs)
  }, [selectedUploadId, uploads]);

  const fetchBranches = async () => {
    const { data } = await supabase.from('branches').select('id, name');
    if (data) setBranches(data);
  };

  const fetchUploads = async (branchId: string) => {
    setLoading(true);
    const { data, error } = await supabase
      .from('menu_uploads')
      .select('*')
      .eq('branch_id', branchId)
      .order('created_at', { ascending: false });
      
    if (error) {
      toast({ title: 'Error', description: 'Failed to fetch uploads', variant: 'destructive' });
    } else {
      setUploads(data || []);
    }
    setLoading(false);
  };

  const fetchMenuItems = async (branchId: string) => {
    try {
      // Use a join query to fetch items for the branch directly
      // This avoids the 400 error from passing too many IDs or empty arrays
      const { data: items, error } = await supabase
        .from('menu_items')
        .select('id, name, base_price, categories!inner(branch_id)')
        .eq('categories.branch_id', branchId);

      if (error) {
        console.error('Error fetching menu items:', error);
        toast({ title: 'Error', description: 'Failed to fetch menu items', variant: 'destructive' });
        return;
      }
        
      setMenuItems(items || []);
    } catch (err) {
      console.error('Unexpected error fetching menu items:', err);
    }
  };

  const handleAddOverlay = () => {
    const newOverlay: Overlay = {
      id: Math.random().toString(36).substr(2, 9),
      item_id: '',
      type: 'price',
      x: 100,
      y: 100,
      width: 100,
      height: 40,
      fontSize: 24,
      fontFamily: 'Arial',
      color: '#000000',
      fontWeight: 'bold',
      textAlign: 'left'
    };
    setOverlays([...overlays, newOverlay]);
    setSelectedOverlayId(newOverlay.id);
  };

  const handleUpdateOverlay = (id: string, updates: Partial<Overlay>) => {
    setOverlays(overlays.map(o => o.id === id ? { ...o, ...updates } : o));
  };

  const handleDeleteOverlay = () => {
    if (selectedOverlayId) {
      setOverlays(overlays.filter(o => o.id !== selectedOverlayId));
      setSelectedOverlayId(null);
    }
  };

  const handleSave = async () => {
    if (!selectedUploadId) return;
    setSaving(true);
    
    const mapping = {
      width: imageSize.width,
      height: imageSize.height,
      overlays: overlays
    };

    const { error } = await supabase
      .from('menu_uploads')
      .update({ visual_mapping: mapping })
      .eq('id', selectedUploadId);

    if (error) {
      toast({ title: 'Error', description: 'Failed to save mapping', variant: 'destructive' });
    } else {
      toast({ title: 'Success', description: 'Mapping saved successfully' });
    }
    setSaving(false);
  };

  if (authLoading) return <div className="flex items-center justify-center h-screen"><Loader2 className="animate-spin" /></div>;
  
  if (!user || user.id !== SUPER_ADMIN_ID) {
    return <Navigate to="/" replace />;
  }

  const selectedUpload = uploads.find(u => u.id === selectedUploadId);
  const selectedOverlay = overlays.find(o => o.id === selectedOverlayId);

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Sidebar */}
      <div className="w-80 bg-white border-r p-4 flex flex-col gap-4 overflow-y-auto">
        <div className="flex items-center gap-2 mb-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="font-bold text-lg">Visual Mapper</h1>
        </div>

        <div className="space-y-2">
          <Label>Select Branch</Label>
          <Select value={selectedBranchId || ''} onValueChange={setSelectedBranchId}>
            <SelectTrigger>
              <SelectValue placeholder="Select a branch..." />
            </SelectTrigger>
            <SelectContent>
              {branches.map(b => (
                <SelectItem key={b.id} value={b.id}>
                  {b.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Select Menu Upload</Label>
          <Select value={selectedUploadId || ''} onValueChange={setSelectedUploadId} disabled={!selectedBranchId}>
            <SelectTrigger>
              <SelectValue placeholder="Select a file..." />
            </SelectTrigger>
            <SelectContent>
              {uploads.map(u => (
                <SelectItem key={u.id} value={u.id}>
                  {u.file_name} ({new Date(u.created_at || '').toLocaleDateString()})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {selectedUploadId && (
          <>
            <div className="border-t pt-4">
              <h3 className="font-semibold mb-2">Tools</h3>
              
              <div className="flex gap-2 mb-2">
                <Button variant="outline" size="sm" onClick={() => setScale(s => Math.max(0.1, s - 0.1))}>
                  <Minus className="h-4 w-4" />
                </Button>
                <div className="flex items-center justify-center min-w-[3rem] text-xs border rounded">
                  {Math.round(scale * 100)}%
                </div>
                <Button variant="outline" size="sm" onClick={() => setScale(s => Math.min(5, s + 0.1))}>
                  <Plus className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={fitToScreen} title="Fit to Screen">
                  <Maximize className="h-4 w-4" />
                </Button>
              </div>

              <div className="flex gap-2">
                <Button onClick={handleAddOverlay} className="w-full">
                  <Plus className="h-4 w-4 mr-2" /> Add Overlay
                </Button>
              </div>
            </div>

            {selectedOverlay && (
              <Card>
                <CardHeader className="py-3">
                  <CardTitle className="text-sm">Selected Overlay</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="space-y-1">
                    <Label>Type</Label>
                    <Select 
                      value={selectedOverlay.type} 
                      onValueChange={(v: any) => handleUpdateOverlay(selectedOverlay.id, { type: v })}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="price">Price</SelectItem>
                        <SelectItem value="name">Item Name</SelectItem>
                        <SelectItem value="description">Description</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1">
                    <Label>Linked Item</Label>
                    <Select 
                      value={selectedOverlay.item_id} 
                      onValueChange={(v) => handleUpdateOverlay(selectedOverlay.id, { item_id: v })}
                    >
                      <SelectTrigger><SelectValue placeholder="Select item..." /></SelectTrigger>
                      <SelectContent>
                        {menuItems.map(item => (
                          <SelectItem key={item.id} value={item.id}>
                            {item.name} ({item.base_price})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Label>Font Size</Label>
                      <Input 
                        type="number" 
                        value={selectedOverlay.fontSize} 
                        onChange={(e) => handleUpdateOverlay(selectedOverlay.id, { fontSize: Number(e.target.value) })} 
                      />
                    </div>
                    <div className="space-y-1">
                      <Label>Color</Label>
                      <Input 
                        type="color" 
                        value={selectedOverlay.color} 
                        onChange={(e) => handleUpdateOverlay(selectedOverlay.id, { color: e.target.value })} 
                      />
                    </div>
                  </div>

                  <Button variant="destructive" size="sm" onClick={handleDeleteOverlay} className="w-full">
                    <Trash2 className="h-4 w-4 mr-2" /> Delete
                  </Button>
                </CardContent>
              </Card>
            )}

            <Button onClick={handleSave} disabled={saving} className="mt-auto">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Save Mapping
            </Button>
          </>
        )}
      </div>

      {/* Canvas Area */}
      <div ref={containerRef} className="flex-1 bg-gray-200 overflow-auto flex items-center justify-center p-8">
        {selectedUpload && imageUrl ? (
          <div className="bg-white shadow-lg">
            <Stage 
              width={imageSize.width * scale} 
              height={imageSize.height * scale}
              scale={{ x: scale, y: scale }}
              onMouseDown={(e) => {
                // Deselect if clicked on empty area
                const clickedOnEmpty = e.target === e.target.getStage();
                if (clickedOnEmpty) {
                  setSelectedOverlayId(null);
                }
              }}
            >
              <Layer>
                <URLImage src={imageUrl} x={0} y={0} width={imageSize.width} height={imageSize.height} />
                {overlays.map((overlay) => (
                  <React.Fragment key={overlay.id}>
                    {/* The visual representation of the data */}
                    <Text
                      x={overlay.x}
                      y={overlay.y}
                      width={overlay.width}
                      height={overlay.height}
                      text={
                        overlay.item_id 
                          ? (overlay.type === 'price' 
                              ? menuItems.find(i => i.id === overlay.item_id)?.base_price.toString() || '0.00'
                              : menuItems.find(i => i.id === overlay.item_id)?.name || 'Item Name')
                          : 'Select Item'
                      }
                      fontSize={overlay.fontSize}
                      fontFamily={overlay.fontFamily}
                      fill={overlay.color}
                      fontStyle={overlay.fontWeight}
                      align={overlay.textAlign}
                      verticalAlign="middle"
                    />
                    {/* The interactive box */}
                    <OverlayRect
                      shapeProps={overlay}
                      isSelected={overlay.id === selectedOverlayId}
                      onSelect={() => setSelectedOverlayId(overlay.id)}
                      onChange={(newAttrs: any) => handleUpdateOverlay(overlay.id, newAttrs)}
                    />
                  </React.Fragment>
                ))}
              </Layer>
            </Stage>
          </div>
        ) : (
          <div className="text-gray-500 flex flex-col items-center">
            <MousePointer2 className="h-12 w-12 mb-4 opacity-50" />
            <p>Select a menu upload to start mapping</p>
          </div>
        )}
      </div>
    </div>
  );
}
